import type { ScheduleConfig } from '@/frontend/types/scheduler'

// Converts a ScheduleConfig to a 5-field cron expression.
//
// DAILY interval=1, time=06:00 → "0 6 * * *"
// DAILY interval=N, time=06:00 → "0 6 */N * *"
// WEEKLY weekDays=[1,3,5], time=09:00 → "0 9 * * 1,3,5"
// MONTHLY monthDay=15, time=10:30 → "30 10 15 * *"
// MONTHLY interval=N, monthDay=15 → "30 10 15 */N *"
export function scheduleToCron(config: ScheduleConfig): string {
  const [hours, minutes] = config.timeOfDay.split(':').map(Number)
  const minute = isNaN(minutes) ? 0 : minutes
  const hour = isNaN(hours) ? 6 : hours

  if (config.frequency === 'DAILY') {
    const dayField = config.interval > 1 ? `*/${config.interval}` : '*'
    return `${minute} ${hour} ${dayField} * *`
  }

  if (config.frequency === 'WEEKLY') {
    const days = config.weekDays && config.weekDays.length > 0
      ? config.weekDays.sort((a, b) => a - b).join(',')
      : '*'
    return `${minute} ${hour} * * ${days}`
  }

  // MONTHLY
  const day = config.monthDay || 1
  const monthField = config.interval > 1 ? `*/${config.interval}` : '*'
  return `${minute} ${hour} ${day} ${monthField} *`
}

/**
 * Parses a 5-field cron string back into a ScheduleConfig.
 * Returns null when the fixed-time editor cannot represent the expression.
 * Read-only surfaces must never invent a daily schedule for wildcard hours.
 */
export function cronToSchedule(cron: string): ScheduleConfig | null {
  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5) return null

  const [minStr, hourStr, domStr, monthStr, dowStr] = parts
  const minute = boundedInteger(minStr, 0, 59)
  const hour = boundedInteger(hourStr, 0, 23)
  if (minute === null || hour === null) return null

  const timeOfDay = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`

  // WEEKLY: day-of-week is not "*"
  if (dowStr !== '*') {
    if (domStr !== '*' || monthStr !== '*') return null
    const days = dowStr.split(',').map(day => boundedInteger(day, 0, 7))
    if (days.some(day => day === null)) return null
    const weekDays = days.map(day => day === 0 ? 7 : day as number)
    return {
      frequency: 'WEEKLY',
      interval: 1,
      timeOfDay,
      weekDays,
    }
  }

  // MONTHLY: day-of-month is a specific number (not "*" or "*/N")
  if (domStr !== '*' && !domStr.startsWith('*/')) {
    const monthDay = boundedInteger(domStr, 1, 31)
    const interval = fieldStep(monthStr, 12)
    if (monthDay === null || interval === null) return null

    return {
      frequency: 'MONTHLY',
      interval,
      timeOfDay,
      monthDay,
    }
  }

  // DAILY: default case
  const interval = fieldStep(domStr, 31)
  if (monthStr !== '*' || interval === null) return null

  return {
    frequency: 'DAILY',
    interval,
    timeOfDay,
  }
}

/** Generates a Portuguese summary of a schedule configuration */
export function getScheduleSummaryPtBR(config: ScheduleConfig): string {
  const { frequency, interval, weekDays, monthDay, timeOfDay } = config
  const dayNames = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']

  let summary = ''

  if (frequency === 'DAILY') {
    summary += interval === 1 ? 'Diariamente' : `A cada ${interval} dias`
  } else if (frequency === 'WEEKLY') {
    summary += interval === 1 ? 'Semanalmente' : `A cada ${interval} semanas`
    if (weekDays && weekDays.length > 0) {
      const selected = weekDays.sort((a, b) => a - b).map(d => dayNames[d - 1])
      summary += ` (${selected.join(', ')})`
    }
  } else if (frequency === 'MONTHLY') {
    const day = monthDay || 1
    summary += interval === 1
      ? `Mensalmente no dia ${day}`
      : `A cada ${interval} meses no dia ${day}`
  }

  summary += ` às ${timeOfDay}`
  return summary
}

/** Converts a cron string to a Portuguese human-readable summary */
export function cronToSummaryPtBR(cron: string): string {
  // Fast-path para intervalos que o ScheduleConfig (DAILY/WEEKLY/MONTHLY a uma
  // hora fixa) não representa e cairiam no default "Diariamente às 06:00":
  // "0 */N * * *" (a cada N horas) e "*/N * * * *" (a cada N min). O agente
  // cria essas rotinas pelo runtime, então elas chegam à UI de leitura.
  const parts = cron.trim().split(/\s+/)
  if (parts.length === 5) {
    const [minField, hourField, dom, month, dow] = parts
    if (dom === '*' && month === '*' && dow === '*') {
      const hourStep = fieldStep(hourField, 23)
      const minute = boundedInteger(minField, 0, 59)
      if (hourStep !== null && minute !== null) {
        const label = hourStep === 1 ? 'A cada hora' : `A cada ${hourStep} horas`
        return minute === 0 ? label : `${label}, no minuto ${minute}`
      }
      const minuteStep = fieldStep(minField, 59)
      if (hourField === '*' && minuteStep !== null) {
        return minuteStep === 1 ? 'A cada minuto' : `A cada ${minuteStep} min`
      }
    }
  }
  const config = cronToSchedule(cron)
  return config ? getScheduleSummaryPtBR(config) : `Cron: ${cron.trim()}`
}

function boundedInteger(value: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(value)) return null
  const number = Number(value)
  return number >= min && number <= max ? number : null
}

function fieldStep(value: string, max: number): number | null {
  if (value === '*') return 1
  return value.startsWith('*/') ? boundedInteger(value.slice(2), 1, max) : null
}

/** Supported unrestricted hourly/minute cron fields, not elapsed-time intervals. */
export function repeatingCronTimes(cron: string): string[] | null {
  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5 || parts.slice(2).some(part => part !== '*')) return null
  const hours = fieldStep(parts[1], 23)
  const minutes = fieldStep(parts[0], 59)
  const fixedMinute = boundedInteger(parts[0], 0, 59)
  if (hours === null || (minutes === null && fixedMinute === null)) return null
  const times: string[] = []
  for (let hour = 0; hour < 24; hour += hours) {
    if (fixedMinute !== null) times.push(`${String(hour).padStart(2, '0')}:${String(fixedMinute).padStart(2, '0')}`)
    else for (let minute = 0; minute < 60; minute += minutes!) {
      times.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`)
    }
  }
  return times
}
